import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { UpdateAccountModalErrors } from '../UpdateAccountModalErrors'
import { UpdateAccountModalActions } from '../UpdateAccountModalActions'

import { MyDropdown } from 'components/ReactHookForm/MyDropdown'
import { MyTextFieldPassword } from 'components/ReactHookForm/MyTextFieldPassword'
import {
  createSchema,
  EditSecretQuestionFormData
} from 'models/forms/SecretQuestionForm'
import { UpdateAccountServerErrors } from 'hooks/useUpdateAccountHandler'

export type SecretQuestionModalFormProps = {
  existingValues?: {
    question?: string
  }
  loading?: boolean
  onClose: () => void
  onSubmit: (formData: EditSecretQuestionFormData) => void
  serverErrors: UpdateAccountServerErrors
}
export const SecretQuestionModalForm = ({
  existingValues,
  loading,
  onClose,
  onSubmit,
  serverErrors
}: SecretQuestionModalFormProps) => {
  const { t } = useTranslation('editSecretQuestion')

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm<EditSecretQuestionFormData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: existingValues
  })

  // Display all possible questions for given language, along with the user's selected question, but w/o dupes
  const questionsForLanguage = (t('questions', { returnObjects: true }) as string[]) || []
  const questionsWithExisting = [...questionsForLanguage, ...(existingValues?.question ? [existingValues.question] : [])]
  const uniqueQuestions = questionsWithExisting.filter((item, index) => questionsWithExisting.indexOf(item) === index)
  const questionOpts = [{ label: '', value: '' }, ...uniqueQuestions.map(question => ({
    label: question,
    value: question
  }))]

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <fieldset className="ds-c-fieldset ds-u-margin-top--0">
          <p>{t('description')}</p>
          <MyDropdown
            name="question"
            label={t('question.label')}
            labelClassName="ds-c-label ds-u-margin-bottom--1"
            options={questionOpts}
          />
          <MyTextFieldPassword
            name="answer"
            label={t(`answer.label`)}
            labelClassName="my-u-text-transform--default"
            defaultValue=""
          />
        </fieldset>

        <UpdateAccountModalErrors
          serverErrors={serverErrors}
          basicErrorText={t('errors.server-basic')}
          invalidCriteriaErrorText={t('errors.server-validation')}
        />
        <UpdateAccountModalActions
          loading={loading}
          submitButtonText={t('submit')}
          submitSiteActivityProps={{
            core: {
              component: 'Account Settings',
              elementId: 'accountsettings_secretsave_click',
              coreClickData: {
                linkText: t('submit')
              }
            }
          }}
          cancelButtonText={t('cancel')}
          handleCancel={onClose}
        />
      </form>
    </FormProvider>
  )
}
